import { PdBaseUI } from "../base/pd-base-ui.js";
import __decorate from "../_virtual/_@oxc-project_runtime@0.147.0/helpers/esm/decorate.js";
import "../pd-button.js";
import { css, html, nothing } from "lit";
import { property, state } from "lit/decorators.js";
import { classMap } from "lit/directives/class-map.js";
//#region src/pd-button-group/PdButtonGroup.ts
/**
* Button group component for single-selection button sets.
*
* @tagname pd-button-group
* @summary Single-selection button group with horizontal or vertical layout.
*
* @event pd-button-group-change - Fired when the user selects another button.
*   Detail: `{ index: number, selected: number[] }` — `index` is the chosen
*   button, `selected` the (single-element) selection array kept for callers
*   that destructure it.
*
* @cssprop --pd-button-group-direction - Flex direction. Default: `row`.
* @cssprop --pd-button-group-width - Group width. Default: `auto`.
* @cssprop --pd-radius-md - Border radius for outer corners. Default: `0.5rem`.
* @cssprop --pd-button-border-radius - Border radius for child buttons. Default: `0`.
* @cssprop --pd-button-width - Width for child buttons. Default: `100%`.
*/
var PdButtonGroup = class extends PdBaseUI {
	constructor(..._args) {
		super(..._args);
		this.buttonList = [];
		this.initValue = [];
		this.vertical = false;
		this._selectedButtons = [];
	}
	static {
		this.styles = [PdBaseUI.styles, css`
      :host {
        display: inline-block;
        --group-direction: var(--pd-button-group-direction, row);
        --group-width: var(--pd-button-group-width, auto);
        --group-border-radius: var(--pd-radius-md);
      }

      .button-group {
        display: flex;
        flex-direction: var(--group-direction);
        width: var(--group-width);
        --pd-button-width: 100%;
        --pd-button-border-radius: 0;
      }

      /* Horizontal layout (default): rounded left/right edges */
      .button-group pd-button:first-child {
        --pd-button-border-radius: var(--group-border-radius) 0 0
          var(--group-border-radius);
      }

      .button-group pd-button:last-child {
        --pd-button-border-radius: 0 var(--group-border-radius)
          var(--group-border-radius) 0;
      }

      .button-group pd-button:only-child {
        --pd-button-border-radius: var(--group-border-radius);
      }

      /* Vertical layout: rounded top/bottom edges */
      .button-group.vertical {
        flex-direction: column;
      }

      .button-group.vertical pd-button:first-child {
        --pd-button-border-radius: var(--group-border-radius)
          var(--group-border-radius) 0 0;
      }

      .button-group.vertical pd-button:last-child {
        --pd-button-border-radius: 0 0 var(--group-border-radius)
          var(--group-border-radius);
      }

      .button-group.vertical pd-button:only-child {
        --pd-button-border-radius: var(--group-border-radius);
      }
    `];
	}
	willUpdate(changedProps) {
		if (changedProps.has("initValue") && this.initValue?.length > 0) {
			if (!gleicheWerte(changedProps.get("initValue"), this.initValue)) this._selectedButtons = [...this.initValue];
		}
	}
	reset(selectedValue) {
		this._selectedButtons = [selectedValue];
	}
	render() {
		const groupClasses = {
			"button-group": true,
			vertical: this.vertical
		};
		return html`
      <div
        class="${classMap(groupClasses)}"
        role="group"
        aria-label="${this.groupLabel || nothing}"
      >
        ${this.buttonList.map((data, index) => html`
            <pd-button
              ?selected="${this._selectedButtons.includes(index)}"
              text="${data.text || ""}"
              icon="${data.pdIcon || ""}"
              ?disabled="${data.disabled === true}"
              @button-clicked="${() => this.onClick(index)}"
            ></pd-button>
          `)}
      </div>
    `;
	}
	onClick(index) {
		if (this._selectedButtons.includes(index)) return;
		this._selectedButtons = [index];
		this.dispatchEvent(new CustomEvent("pd-button-group-change", {
			detail: {
				index,
				selected: this._selectedButtons
			},
			bubbles: true,
			composed: true
		}));
	}
};
__decorate([property({ type: Array })], PdButtonGroup.prototype, "buttonList", void 0);
__decorate([property({ type: Array })], PdButtonGroup.prototype, "initValue", void 0);
__decorate([property({
	type: Boolean,
	reflect: true
})], PdButtonGroup.prototype, "vertical", void 0);
__decorate([property({
	type: String,
	attribute: "group-label"
})], PdButtonGroup.prototype, "groupLabel", void 0);
__decorate([state()], PdButtonGroup.prototype, "_selectedButtons", void 0);
function gleicheWerte(a, b) {
	return !!a && a.length === b.length && a.every((v, i) => v === b[i]);
}
//#endregion
export { PdButtonGroup };
